/**
 * Client HTTP di `/api/v2/`.
 * ---------------------------------------------------------------------------
 * Unico punto dell'applicazione che parla via rete. Nessun componente fa
 * `fetch` da sé: quando il contratto cambia, cambia questo file.
 *
 * Autenticazione a cookie httpOnly (decisione 43): i token non passano di
 * qui — il browser li allega da solo con `credentials: same-origin`. Il
 * client aggiunge `X-CSRFToken` alle scritture e, su un 401 di una richiesta
 * autenticata, rinnova la sessione **una volta** e ripete la richiesta.
 * Il rinnovo è deduplicato in `auth.ts`.
 *
 * Due sorgenti, scelte da `VITE_DATA_SOURCE`:
 *   · `api`      -> `${VITE_API_BASE}` (default `/api/v2`), Django + DRF
 *   · `fixtures` -> `/dev-data/*.json`, copia dei dataset reali del portale
 *                   statico. Serve a sviluppare e testare senza backend.
 */

import { csrfToken, refreshSession } from './auth';
import { erroriPerCampo } from './types';
import type { CodiceErrore, ErroriPerCampo, Paginated, RispostaErrore } from './types';

export type DataSource = 'api' | 'fixtures';

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api/v2';
export const DATA_SOURCE: DataSource =
  (import.meta.env.VITE_DATA_SOURCE as DataSource | undefined) ?? 'fixtures';

/**
 * La sorgente dati letta **al momento della chiamata**, non all'import.
 *
 * Serve dove la scelta decide se una richiesta parte o no (menu e home
 * configurabili, decisione 82): con la costante valutata all'import quel
 * ramo non sarebbe verificabile in test, perché il valore sarebbe già stato
 * congelato prima che il test possa dire quale scenario sta provando.
 */
export function sorgenteDati(): DataSource {
  return (import.meta.env.VITE_DATA_SOURCE as DataSource | undefined) ?? 'fixtures';
}

/**
 * Errore dell'API.
 *
 * Trasporta **lo stato HTTP e il codice applicativo**. Il codice è la cosa su
 * cui si rami­fica: distinguere `transizione_non_valida` da
 * `email_non_configurata` guardando il messaggio significherebbe fare pattern
 * matching su una frase in italiano, che si rompe appena qualcuno la
 * riformula.
 */
export class ApiError extends Error {
  readonly status: number;
  /** `errore.codice`. Stringa vuota se la risposta non è nel formato atteso. */
  readonly codice: CodiceErrore;
  /** `errore.dettagli`. Per `validazione` è `{campo: [messaggi]}`. */
  readonly dettagli: Readonly<Record<string, unknown>>;
  /** Corpo grezzo, per diagnosticare le risposte fuori contratto. */
  readonly body: unknown;

  constructor(
    status: number,
    message: string,
    codice: CodiceErrore = '',
    dettagli: Readonly<Record<string, unknown>> = {},
    body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.codice = codice;
    this.dettagli = dettagli;
    this.body = body;
  }

  /** Errore di rete o CORS: status 0. Distinto da un 5xx del server. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
  get isNotFound(): boolean {
    return this.status === 404 || this.codice === 'not_found';
  }
  get isUnauthorized(): boolean {
    return this.status === 401 || this.codice === 'not_authenticated';
  }
  get isForbidden(): boolean {
    return this.status === 403 || this.codice === 'permission_denied';
  }
  /** 400 con `dettagli` = `{campo: [messaggi]}`. */
  get isValidation(): boolean {
    return this.status === 400 || this.codice === 'validazione';
  }
  /** 409: transizione di stato non ammessa nella coda di moderazione. */
  get isConflict(): boolean {
    return this.status === 409;
  }
  /**
   * 429. Dichiarato su **tutti e 33** gli endpoint: 200 richieste/minuto sugli
   * endpoint pubblici, più stretto sull'invio di contributi. È quindi l'errore
   * che il sito pubblico incontrerà più spesso, e va gestito con un messaggio
   * suo — non con "errore generico".
   */
  get isThrottled(): boolean {
    return this.status === 429 || this.codice === 'throttled';
  }
  /**
   * 503 `email_non_configurata`. La decisione 24 rinvia la configurazione
   * email e il backend rende l'assenza **esplicita** invece di fingere l'invio.
   * È uno stato che si vedrà davvero nel back office finché non si configura:
   * merita un messaggio che dica cosa manca.
   */
  get isEmailNonConfigurata(): boolean {
    return this.codice === 'email_non_configurata' || this.status === 503;
  }
  /** 410 Gone: la dismissione di `/api/v1/`. */
  get isGone(): boolean {
    return this.status === 410;
  }

  /** Errori di validazione per campo, pronti per `<ErrorSummary>`. */
  get erroriCampo(): ErroriPerCampo {
    return erroriPerCampo(this.dettagli);
  }
}

/** Valori ammessi in querystring. Gli array diventano parametri ripetuti. */
export type QueryValue = string | number | boolean | readonly string[] | undefined | null;

export interface RequestOptions {
  readonly signal?: AbortSignal;
  /**
   * Lingua da negoziare via `Accept-Language`.
   * ⚠️ Lo schema del backend **non dichiara** la negoziazione della lingua:
   * l'header viene mandato ma non è detto che venga onorato. Punto aperto
   * n. 9 in `../docs/contratto-api-v2.md`.
   */
  readonly language?: string;
  readonly query?: Readonly<Record<string, QueryValue>>;
  /** Allega il token JWT e gestisce il refresh su 401. */
  readonly authenticated?: boolean;
  /**
   * Header aggiuntivi. Serve a `If-Match` (decisione 81): la dashboard
   * rimanda l'`ETag` letto sulla GET di dettaglio, e il server risponde 409
   * `conflitto_concorrenza` invece di sovrascrivere il lavoro di un altro.
   */
  readonly headers?: Readonly<Record<string, string>>;
}

/**
 * Risposta con gli header che contano.
 *
 * L'`ETag` non è un dettaglio di trasporto: è il lock ottimistico del
 * contratto redazionale. Senza un modo di leggerlo, ogni salvataggio della
 * dashboard sovrascriverebbe in silenzio (punto 3 della sezione 21 del
 * contratto), quindi il client lo espone al chiamante invece di scartarlo.
 */
export interface RispostaConEtag<T> {
  readonly dati: T;
  readonly etag: string | null;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const rel = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${rel}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, item);
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

/**
 * Costruisce un `ApiError` dalla risposta, leggendo la busta
 * `{errore: {codice, messaggio, dettagli}}`.
 *
 * Se la risposta non è in quel formato (proxy, errore del server web, HTML di
 * cortesia) non si inventa un codice: si conserva il corpo grezzo e si usa lo
 * stato HTTP. Meglio un errore onestamente generico che uno finto specifico.
 */
async function buildApiError(response: Response, path: string): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Risposta non JSON: resta solo lo stato.
  }

  const busta = body as Partial<RispostaErrore> | null;
  const errore = busta?.errore;

  if (errore && typeof errore.messaggio === 'string') {
    return new ApiError(
      response.status,
      errore.messaggio,
      (errore.codice as CodiceErrore) ?? '',
      errore.dettagli ?? {},
      body,
    );
  }

  return new ApiError(
    response.status,
    `${response.status} su ${path}`,
    '',
    {},
    body,
  );
}

const METODI_DI_SCRITTURA = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function buildHeaders(options: RequestOptions, metodo: string): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.language) headers['Accept-Language'] = options.language;
  if (METODI_DI_SCRITTURA.has(metodo)) {
    // Autenticazione via cookie ⇒ le scritture esigono il token CSRF
    // (decisione 43). Sulle richieste anonime l'header in più non nuoce.
    const token = csrfToken();
    if (token) headers['X-CSRFToken'] = token;
  }
  if (options.headers) Object.assign(headers, options.headers);
  return headers;
}

/**
 * Il motore unico delle richieste. Nessuna riprova automatica sugli errori di
 * rete: la decide il chiamante. L'unica ripetizione è quella dopo il rinnovo
 * della sessione su un 401 (una volta sola, deduplicata in `auth.ts`).
 */
async function richiestaGrezza(
  metodo: string,
  path: string,
  body: unknown | undefined,
  options: RequestOptions = {},
): Promise<Response> {
  const url = buildUrl(path, options.query);
  const useAuth = options.authenticated === true;

  const send = (): Promise<Response> =>
    fetch(url, {
      method: metodo,
      headers: {
        ...buildHeaders(options, metodo),
        ...(body !== undefined && !(body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
      },
      ...(body !== undefined
        ? { body: body instanceof FormData ? body : JSON.stringify(body) }
        : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      credentials: 'same-origin',
    });

  let response: Response;
  try {
    response = await send();
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, `Rete non raggiungibile per ${path}`);
  }

  // 401 su richiesta autenticata: UN rinnovo di sessione, poi si ripete.
  if (response.status === 401 && useAuth) {
    const rinnovata = await refreshSession();
    if (rinnovata) {
      try {
        response = await send();
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
        throw new ApiError(0, `Rete non raggiungibile per ${path}`);
      }
    }
  }

  if (!response.ok) throw await buildApiError(response, path);
  return response;
}

/**
 * Il corpo della risposta, già spacchettato. `204` (e `205`) non hanno corpo:
 * leggerlo darebbe un errore di parsing su una richiesta andata a buon fine.
 */
async function corpo<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.status === 205) return undefined as T;
  return (await response.json()) as T;
}

async function richiesta<T>(
  metodo: string,
  path: string,
  body: unknown | undefined,
  options: RequestOptions = {},
): Promise<T> {
  return corpo<T>(await richiestaGrezza(metodo, path, body, options));
}

/**
 * Come `richiesta`, ma conserva l'`ETag`.
 *
 * Nei test `fetch` è spesso stubbato con un oggetto minimo senza `headers`:
 * l'accesso è guardato, un `ETag` assente vale `null` — è un lock che non c'è,
 * non un guasto.
 */
async function richiestaConEtag<T>(
  metodo: string,
  path: string,
  body: unknown | undefined,
  options: RequestOptions = {},
): Promise<RispostaConEtag<T>> {
  const response = await richiestaGrezza(metodo, path, body, options);
  return {
    dati: await corpo<T>(response),
    etag: response.headers?.get?.('ETag') ?? null,
  };
}

/** GET su `/api/v2/`. */
export async function apiGet<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return richiesta<T>('GET', path, undefined, options);
}

/** POST su `/api/v2/`. Accetta anche `FormData` (upload multipart). */
export async function apiPost<T>(
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<T> {
  return richiesta<T>('POST', path, body, options);
}

export async function apiPut<T>(
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<T> {
  return richiesta<T>('PUT', path, body, options);
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<T> {
  return richiesta<T>('PATCH', path, body, options);
}

export async function apiDelete<T = void>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return richiesta<T>('DELETE', path, undefined, options);
}

/**
 * `OPTIONS` su un endpoint.
 *
 * Serve a UNA cosa sola: leggere i metadati dei campi che DRF pubblica in
 * `actions.POST` (tipo, obbligatorietà, `choices`), con cui l'editor generico
 * della dashboard costruisce form veri invece di caselle di testo. Non è
 * documentato nel contratto — è comportamento standard di DRF — quindi il
 * chiamante lo tratta come arricchimento facoltativo.
 */
export async function apiOptions<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return richiesta<T>('OPTIONS', path, undefined, options);
}

// --- Varianti che conservano l'ETag (decisione 81) -------------------------
//
// Servono SOLO alla dashboard redazionale: il portale pubblico legge e basta,
// e un lock ottimistico su una GET anonima non significherebbe niente.

export function apiGetConEtag<T>(
  path: string,
  options: RequestOptions = {},
): Promise<RispostaConEtag<T>> {
  return richiestaConEtag<T>('GET', path, undefined, options);
}

export function apiPatchConEtag<T>(
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<RispostaConEtag<T>> {
  return richiestaConEtag<T>('PATCH', path, body, options);
}

export function apiPutConEtag<T>(
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<RispostaConEtag<T>> {
  return richiestaConEtag<T>('PUT', path, body, options);
}

export function apiPostConEtag<T>(
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<RispostaConEtag<T>> {
  return richiestaConEtag<T>('POST', path, body, options);
}

/** GET su una fixture statica in `/dev-data/`. Stessa gestione errori. */
export async function fixtureGet<T>(file: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/dev-data/${file}`, signal ? { signal } : {});
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      0,
      `Fixture ${file} non raggiungibile. Esegui: npm run fixtures:seed`,
    );
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Fixture ${file} assente (${response.status}). Esegui: npm run fixtures:seed`,
    );
  }
  return (await response.json()) as T;
}

/** Busta `{meta, risultati}` costruita da un array già in memoria. */
export function paginatedDa<T>(risultati: readonly T[]): Paginated<T> {
  return {
    meta: {
      totale: risultati.length,
      pagina: 1,
      pagine: risultati.length > 0 ? 1 : 0,
      per_pagina: risultati.length,
      prossima: null,
      precedente: null,
    },
    risultati,
  };
}

/** Busta di paginazione vuota. */
export function paginatedVuoto<T>(): Paginated<T> {
  return paginatedDa<T>([]);
}
